import React, { useEffect, useState } from 'react';
import { Flex } from 'antd';
import { DateTime } from 'luxon';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { MsTeamsTargetingClient, SlackTargetingClient, StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExExpandableAlert, DevExExpandableAlertType } from 'jf/components/DevExExpandableAlert';
import { DevExModal } from 'jf/components/DevExModal';
import { DevExText } from 'jf/components/DevExText';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { formatISO } from 'jf/utils/timeUtils';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { useStudyParticipation } from './useStudyParticipation';

export const SendStudyReminderModal: React.FC = () => {
  const modal = useJFOverlay(SendStudyReminderModal);
  const { studyRef, teamRef } = useParams<{ studyRef: string; teamRef?: string }>();
  const theme = useDevExTheme();
  const queryClient = useQueryClient();

  const [isSending, setIsSending] = useState(false);

  const { msTeamsTargeting } = useClientFlags();
  const { useGetStudyTargets, msgProviderLower, msgProvider } = useTargetingClient();

  const { mutateAsync: sendStudySlackMessages } = useClientMutation(
    SlackTargetingClient.sendStudyMessages
  );
  const { mutateAsync: sendStudyMsteamsMessages } = useClientMutation(
    MsTeamsTargetingClient.sendStudyMsteamsMessages
  );

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef });

  const { data: participation } = useStudyParticipation({ studyRef, teamRef });

  const { data: targets } = useGetStudyTargets({ studyRef, isEnabled: true });

  const onSend = async () => {
    setIsSending(true);
    if (msTeamsTargeting) {
      await sendStudyMsteamsMessages({ studyRef: studyRef! });
    } else {
      await sendStudySlackMessages({ studyRef: studyRef! });
    }
    queryClient.invalidateQueries(['GET_STUDY', studyRef]);

    if (participation && targets) {
      const responseRate = (participation.uniqueResponses / targets.length).toFixed(1);
      trackEvent(`survey-analyzer:send-${msgProviderLower}-reminder:send`, { responseRate });
    }

    setIsSending(false);
    modal.close();
  };

  useEffect(() => {
    setIsSending(false);
  }, [modal.isOpened]);

  const sentAt = study?.sentAt && DateTime.fromISO(study.sentAt);
  const sentAgoMinutes = sentAt ? DateTime.now().diff(sentAt, 'minute').minutes : 0;

  return (
    <DevExModal
      open={modal.isOpened}
      onCancel={() => modal.close()}
      title="Send reminder"
      width={600}
      footer={
        <Flex vertical align="end" gap={theme.variable.spacing.sm}>
          <DevExText type="secondary" fontSize="xs">
            Last sent {formatISO(study?.sentAt, DateTime.DATETIME_SHORT)}
          </DevExText>

          <DevExButton
            type="primary"
            loading={isSending}
            onClick={() => {
              trackEvent(`manage-respondents:send-${msgProviderLower}-reminder:click`, {
                surveyRef: studyRef,
              });
              onSend();
            }}
          >
            Send
          </DevExButton>
        </Flex>
      }
    >
      <Flex vertical gap={theme.variable.spacing.md}>
        <div>
          Send a reminder in {msgProvider} to survey recipients that have not yet responded.
        </div>

        {sentAgoMinutes < 1440 && (
          <DevExExpandableAlert
            title={`The last ${msgProvider} message was sent less than 24 hours ago. We recommend waiting before sending a new reminder.`}
            type={DevExExpandableAlertType.WARNING}
          />
        )}
      </Flex>
    </DevExModal>
  );
};
